import * as signalR from '@microsoft/signalr';

export interface ShippingActivityEvent {
  eventType: 'LabelCreated' | 'LabelVoided' | 'PickTicketCreated' | 'PickTicketShipped' | 'PickTicketUnshipped' | 'PickTicketPrinted';
  pickTicketId: number | null;
  orderId: number;
  userId: number | null;
  timestamp: string;
  isReturnLabel?: boolean;
}

export type ShippingActivityCallback = (event: ShippingActivityEvent) => void;

class ShippingActivitySignalRService {
  private connection: signalR.HubConnection | null = null;
  private callbacks: Set<ShippingActivityCallback> = new Set();
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  /**
   * Initialize the SignalR connection to the update hub
   */
  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl('/updateHub')
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      // Handle reconnection events
      this.connection.onreconnecting((error) => {
        console.log('[ShippingActivitySignalR] Reconnecting...', error?.message);
      });

      this.connection.onreconnected((connectionId) => {
        console.log('[ShippingActivitySignalR] Reconnected:', connectionId);
        this.reconnectAttempts = 0;
      });

      this.connection.onclose((error) => {
        console.log('[ShippingActivitySignalR] Connection closed:', error?.message);
        this.handleConnectionClosed();
      });

      // Subscribe to ShippingActivityUpdated events
      this.connection.on('ShippingActivityUpdated', (event: ShippingActivityEvent) => {
        console.log('[ShippingActivitySignalR] Received event:', event);
        this.notifyCallbacks(event);
      });

      await this.connection.start();
      console.log('[ShippingActivitySignalR] Connected successfully');
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('[ShippingActivitySignalR] Connection failed:', error);
      this.handleConnectionClosed();
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Handle connection closure and attempt reconnection
   */
  private handleConnectionClosed(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.callbacks.size > 0) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      console.log(`[ShippingActivitySignalR] Will attempt reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
      setTimeout(() => this.connect(), delay);
    }
  }

  /**
   * Subscribe to shipping activity events
   * @param callback Function to call when an event is received
   * @returns Unsubscribe function
   */
  subscribe(callback: ShippingActivityCallback): () => void {
    this.callbacks.add(callback);

    // Connect if not already connected
    if (this.connection?.state !== signalR.HubConnectionState.Connected && !this.isConnecting) {
      this.connect();
    }

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);

      // Disconnect if no more subscribers
      if (this.callbacks.size === 0) {
        this.disconnect();
      }
    };
  }

  /**
   * Notify all callbacks of an event
   */
  private notifyCallbacks(event: ShippingActivityEvent): void {
    this.callbacks.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('[ShippingActivitySignalR] Callback error:', error);
      }
    });
  }

  /**
   * Disconnect from the SignalR hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('[ShippingActivitySignalR] Disconnected');
      } catch (error) {
        console.error('[ShippingActivitySignalR] Disconnect error:', error);
      }
      this.connection = null;
    }
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Singleton instance
let serviceInstance: ShippingActivitySignalRService | null = null;

export function getShippingActivitySignalR(): ShippingActivitySignalRService {
  if (!serviceInstance) {
    serviceInstance = new ShippingActivitySignalRService();
  }
  return serviceInstance;
}

export default ShippingActivitySignalRService;
